<script lang="ts">
  import { createEventDispatcher, onDestroy } from "svelte";
  import Buttons from "../Buttons.svelte";
  import ConfirmDialog from "../../ConfirmDialog.svelte";
  import { parseQuestionMetadata } from "../../../stores/BriefStepStore.js";

  function autofocus(node: HTMLElement, enabled = true) {
    if (enabled) node.focus();
    return {};
  }

  export let answer = "";
  export let stepData: any;
  export let displayReport = false;

  const dispatch = createEventDispatcher();

  let autoSaveTimeout: ReturnType<typeof setTimeout> | null = null;
  let lastSavedAnswer = answer;
  let showGenerateConfirm = false;
  let otherText = "";
  let fieldValues: Record<string, string> = {};
  let addonValues: Record<string, string> = {};
  let bodyAnswer = "";

  $: meta = parseQuestionMetadata(stepData?.question_metadata);
  $: questionType = String(stepData?.question_type || "text");
  $: helper = String(meta.helper || "");
  $: example = String(meta.example || "");
  $: encouragement = String(meta.encouragement || "");
  $: placeholder = String(meta.placeholder || "");
  $: allowUnknown = Boolean(meta.allow_unknown);
  $: unknownLabel = String(meta.unknown_label || "I don't know");
  $: optional = Boolean(meta.optional);
  $: followUp = String(meta.follow_up || "");
  $: chipOptions = Array.isArray(meta.options) && questionType === "chips"
    ? (meta.options as string[])
    : [];
  $: choiceOptions = Array.isArray(meta.options) && questionType === "choice"
    ? (meta.options as Array<{ value: string; label: string; hint?: string }>)
    : [];
  type OptionDef = string | { value?: string; label?: string; hint?: string };
  type FieldDef = { key: string; label: string; optional?: boolean; placeholder?: string; options?: OptionDef[] };
  type AddonDef = { key: string; label: string; optional?: boolean; placeholder?: string };

  $: fieldDefs = Array.isArray(meta.fields)
    ? (meta.fields as FieldDef[])
    : [];
  $: addonDefs = Array.isArray(meta.addons)
    ? (meta.addons as AddonDef[])
    : [];
  $: allowOther = Boolean(meta.allow_other);
  $: selectedChip = chipValue(answer);
  $: isOtherChip = selectedChip === "Other" || (allowOther && selectedChip.startsWith("Other:"));
  $: canContinue = optional || hasUsableAnswer(answer, questionType);

  $: if (questionType === "fields" && fieldDefs.length) {
    fieldValues = parseFields(answer, fieldDefs);
  }

  $: if (questionType === "text" && addonDefs.length) {
    const split = splitAddons(answer, addonDefs);
    bodyAnswer = split.body;
    addonValues = split.values;
  }

  $: if (questionType === "chips" && isOtherChip && answer && !otherText) {
    otherText = answer.replace(/^Other:\s*/i, "");
  }

  function chipValue(value: string): string {
    if (!value) return "";
    if (value.startsWith("Other:")) return "Other";
    return value;
  }

  function splitAddons(value: string, defs: AddonDef[]): { body: string; values: Record<string, string> } {
    const values: Record<string, string> = {};
    defs.forEach((addon) => {
      values[addon.key] = "";
    });
    if (!value) return { body: "", values };
    const bodyLines: string[] = [];
    value.split("\n").forEach((line) => {
      const match = line.match(/^([^:]+):\s*(.*)$/);
      if (!match) {
        bodyLines.push(line);
        return;
      }
      const label = match[1].trim().toLowerCase();
      const addon = defs.find((item) => item.label.toLowerCase() === label);
      if (addon) values[addon.key] = match[2];
      else bodyLines.push(line);
    });
    return { body: bodyLines.join("\n").replace(/\n+$/, ""), values };
  }

  function composeAddons(body: string, values: Record<string, string>, defs: AddonDef[]): string {
    const lines = [body.trim()];
    defs.forEach((addon) => {
      const value = (values[addon.key] || "").trim();
      if (value) lines.push(`${addon.label}: ${value}`);
    });
    return lines.filter((line) => line !== "").join("\n");
  }

  function parseFields(value: string, defs: FieldDef[]): Record<string, string> {
    const next: Record<string, string> = {};
    defs.forEach((field) => {
      next[field.key] = "";
    });
    if (!value) return next;
    value.split("\n").forEach((line) => {
      const match = line.match(/^([^:]+):\s*(.*)$/);
      if (!match) return;
      const label = match[1].trim().toLowerCase();
      const def = defs.find((field) => field.label.toLowerCase().startsWith(label) || field.key === label);
      if (def) next[def.key] = match[2];
    });
    return next;
  }

  function serializeFields(values: Record<string, string>, defs: FieldDef[], fillUnknown = false): string {
    return defs
      .map((field) => {
        const value = (values[field.key] || "").trim();
        if (!value && field.optional) return "";
        return `${field.label}: ${value || (fillUnknown && !field.optional ? "I don't know" : "")}`;
      })
      .filter((line) => line !== "" && !line.endsWith(": "))
      .join("\n");
  }

  function hasUsableAnswer(value: string, type: string): boolean {
    const trimmed = (value || "").trim();
    if (!trimmed) return false;
    if (type === "chips" && trimmed === "Other") return false;
    if (type === "fields") {
      return trimmed.split("\n").some((line) => {
        const part = line.split(":").slice(1).join(":").trim();
        return part.length > 0;
      });
    }
    return true;
  }

  function queueSave(next: string) {
    answer = next;
    if (autoSaveTimeout) clearTimeout(autoSaveTimeout);
    autoSaveTimeout = setTimeout(() => {
      if (next !== lastSavedAnswer) {
        lastSavedAnswer = next;
        dispatch("autosave", { answer: next });
      }
    }, 800);
  }

  function setText(value: string) {
    queueSave(value);
  }

  function selectChip(option: string) {
    if (option === "Other") {
      queueSave(otherText.trim() ? `Other: ${otherText.trim()}` : "Other");
      return;
    }
    otherText = "";
    queueSave(option);
  }

  function setOther(value: string) {
    otherText = value;
    queueSave(value.trim() ? `Other: ${value.trim()}` : "Other");
  }

  function selectChoice(value: string) {
    queueSave(value);
  }

  function setField(key: string, value: string) {
    fieldValues = { ...fieldValues, [key]: value };
    queueSave(serializeFields(fieldValues, fieldDefs));
  }

  function optionLabel(option: OptionDef): string {
    return typeof option === "string" ? option : String(option.label || option.value || "");
  }

  function optionHint(option: OptionDef): string {
    return typeof option === "string" ? "" : String(option.hint || "");
  }

  function toggleFieldOption(key: string, option: string) {
    const current = fieldValues[key] || "";
    setField(key, current === option ? "" : option);
  }

  function setBody(value: string) {
    bodyAnswer = value;
    queueSave(composeAddons(value, addonValues, addonDefs));
  }

  function setAddon(key: string, value: string) {
    addonValues = { ...addonValues, [key]: value };
    queueSave(composeAddons(bodyAnswer, addonValues, addonDefs));
  }

  function markUnknown() {
    if (questionType === "fields") {
      fieldDefs.forEach((field) => {
        if (field.optional) return;
        if (!(fieldValues[field.key] || "").trim()) {
          fieldValues[field.key] = "I don't know";
        }
      });
      fieldValues = { ...fieldValues };
      queueSave(serializeFields(fieldValues, fieldDefs, true));
      return;
    }
    queueSave(unknownLabel);
  }

  function outgoingAnswer(): string {
    if (questionType === "fields") {
      return serializeFields(fieldValues, fieldDefs, true);
    }
    return answer;
  }

  function handleNavigation(e: CustomEvent<{ dir: string }>) {
    const { dir } = e.detail;
    if (dir === "next" && !canContinue && !optional) {
      return;
    }
    if (dir === "next" && displayReport) {
      showGenerateConfirm = true;
      return;
    }
    dispatch("navigate", { dir, answer: outgoingAnswer() });
  }

  function skipOptional() {
    dispatch("navigate", { dir: "next", answer: "" });
  }

  function confirmGenerate() {
    showGenerateConfirm = false;
    dispatch("navigate", { dir: "next", answer: outgoingAnswer() });
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey && questionType !== "text" && questionType !== "fields") {
      e.preventDefault();
      if (canContinue) dispatch("navigate", { dir: "next", answer });
    }
  }

  onDestroy(() => {
    if (autoSaveTimeout) clearTimeout(autoSaveTimeout);
  });
</script>

<svelte:window on:keydown={handleKeydown} />

<div class="question">
  {#if stepData.question_prefix}
    <p class="prefix">{stepData.question_prefix}</p>
  {/if}
  <h2 class="title">{stepData.question_content}</h2>
  {#if helper}
    <p class="helper">{helper}</p>
  {/if}
  {#if example}
    <p class="example">For example — {example}</p>
  {/if}
  {#if encouragement}
    <p class="coach">{encouragement}</p>
  {/if}

  {#if questionType === "chips"}
    <div class="chips" role="group" aria-label={stepData.question_content}>
      {#each chipOptions as option (option)}
        <button
          type="button"
          class="chip"
          class:selected={selectedChip === option}
          on:click={() => selectChip(option)}
        >
          {option}
        </button>
      {/each}
    </div>
    {#if isOtherChip}
      <label class="sr-only" for="chip-other">Other territory</label>
      <input
        id="chip-other"
        type="text"
        use:autofocus
        value={otherText}
        on:input={(e) => setOther((e.currentTarget as HTMLInputElement).value)}
        placeholder="What is it, then?"
      />
    {/if}
  {:else if questionType === "choice"}
    <div class="choices" role="radiogroup" aria-label={stepData.question_content}>
      {#each choiceOptions as option (option.value)}
        <button
          type="button"
          class="choice"
          class:selected={answer === option.value}
          role="radio"
          aria-checked={answer === option.value}
          on:click={() => selectChoice(option.value)}
        >
          <strong>{option.label}</strong>
          {#if option.hint}
            <span>{option.hint}</span>
          {/if}
        </button>
      {/each}
    </div>
  {:else if questionType === "fields"}
    <div class="fields">
      {#each fieldDefs as field (field.key)}
        {#if field.optional && field.key === fieldDefs.find((item) => item.optional)?.key}
          <p class="optional-note">Optional. Skip either of these.</p>
        {/if}
        <label for={`field-${field.key}`}>{field.label}</label>
        {#if Array.isArray(field.options) && field.options.length > 0}
          <div class="chips" role="group" aria-label={field.label}>
            {#each field.options as option (optionLabel(option))}
              <button
                type="button"
                class="chip"
                class:selected={fieldValues[field.key] === optionLabel(option)}
                aria-pressed={fieldValues[field.key] === optionLabel(option)}
                on:click={() => toggleFieldOption(field.key, optionLabel(option))}
              >
                <span>{optionLabel(option)}</span>
                {#if optionHint(option)}
                  <small>{optionHint(option)}</small>
                {/if}
              </button>
            {/each}
          </div>
        {:else}
          <input
            id={`field-${field.key}`}
            type="text"
            use:autofocus={field.key === fieldDefs[0]?.key}
            value={fieldValues[field.key] || ""}
            on:input={(e) => setField(field.key, (e.currentTarget as HTMLInputElement).value)}
            placeholder={field.placeholder || (field.optional ? "Leave blank if you are not sure" : "I don't know is fine")}
          />
        {/if}
      {/each}
    </div>
  {:else}
    <label class="sr-only" for="answer">{stepData.question_content}</label>
    <textarea
      id="answer"
      use:autofocus
      value={addonDefs.length ? bodyAnswer : answer}
      on:input={(e) => {
        const next = (e.currentTarget as HTMLTextAreaElement).value;
        if (addonDefs.length) setBody(next);
        else setText(next);
      }}
      placeholder={placeholder}
      rows="4"
    ></textarea>
    {#each addonDefs as addon (addon.key)}
      <label for={`addon-${addon.key}`} class="addon-label">{addon.label}</label>
      <textarea
        id={`addon-${addon.key}`}
        rows="3"
        value={addonValues[addon.key] || ""}
        placeholder={addon.placeholder || "Optional"}
        on:input={(e) => setAddon(addon.key, (e.currentTarget as HTMLTextAreaElement).value)}
      ></textarea>
    {/each}
    {#if followUp && (addonDefs.length ? bodyAnswer : answer).trim().length > 12}
      <p class="follow-up">{followUp}</p>
    {/if}
  {/if}

  <div class="footer">
    {#if allowUnknown}
      <button type="button" class="unknown" on:click={markUnknown}>
        {unknownLabel}
      </button>
    {:else if optional}
      <button type="button" class="unknown" on:click={skipOptional}>
        Skip
      </button>
    {:else}
      <span></span>
    {/if}

    <div class="nav">
      <Buttons
        {stepData}
        {displayReport}
        nextDisabled={!canContinue && !optional}
        on:navigate={handleNavigation}
        onGenerateBriefClick={() => (showGenerateConfirm = true)}
      />
    </div>
  </div>

  {#if !canContinue && !optional}
    <p class="need-answer" role="status">Give me something here, even a hunch.</p>
  {/if}
</div>

<ConfirmDialog
  bind:show={showGenerateConfirm}
  title="Ready for the brief?"
  message="I will sit with what you said and write it as a researcher would. This is not a restatement of your answers."
  confirmText="Write the brief"
  cancelText="Not yet"
  type="info"
  on:confirm={confirmGenerate}
  on:cancel={() => (showGenerateConfirm = false)}
/>

<style>
  .question {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .prefix {
    margin: 0;
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--primary);
    letter-spacing: 0.02em;
  }

  .title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 500;
    color: var(--grey);
    line-height: 1.3;
    text-align: left;
  }

  .helper,
  .example,
  .follow-up,
  .need-answer {
    margin: 0;
    color: var(--grey-secondary);
    font-size: 0.95rem;
    text-align: left;
  }

  .example {
    font-style: italic;
  }

  .coach {
    display: inline-flex;
    margin: 0.85rem 0 1.15rem;
    padding: 0.4rem 0.8rem;
    border-radius: 999px;
    background: color-mix(in srgb, #45c496 18%, transparent);
    color: #1f7a5c;
  }

  .chip small {
    display: block;
    font-weight: 400;
    color: var(--grey-secondary);
  }

  .follow-up {
    color: var(--primary);
  }

  textarea,
  input[type="text"] {
    width: 100%;
    box-sizing: border-box;
    padding: 0.75rem 1rem;
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius-button);
    font-size: inherit;
    background-color: rgb(var(--input-bg));
    color: var(--grey);
    resize: vertical;
  }

  textarea:focus,
  input[type="text"]:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .chip,
  .choice,
  .unknown {
    border: 0;
    cursor: pointer;
    font-weight: 500;
  }

  .chip {
    padding: 0.5rem 0.9rem;
    border-radius: 999px;
    background: var(--white);
    color: var(--grey);
    box-shadow: inset 0 0 0 1px var(--border-color);
  }

  .chip.selected {
    background: var(--primary);
    color: var(--color-bright);
    box-shadow: none;
  }

  .choices {
    display: grid;
    gap: 0.75rem;
  }

  .choice {
    text-align: left;
    padding: 1rem 1.1rem;
    border-radius: var(--radius-md, 8px);
    background: var(--white);
    color: var(--grey);
    box-shadow: inset 0 0 0 1px var(--border-color);
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .choice span {
    color: var(--grey-secondary);
    font-weight: 400;
  }

  .choice.selected {
    box-shadow: inset 0 0 0 2px var(--primary);
  }

  .fields {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .fields label,
  .addon-label {
    font-weight: 400;
    color: var(--grey);
    margin-top: 0.5rem;
    text-align: left;
  }

  .optional-note {
    margin: 0.75rem 0 0 0;
    font-size: 0.85rem;
    color: var(--grey-secondary);
    text-align: left;
  }

  .footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    margin-top: 0.75rem;
  }

  .unknown {
    background: transparent;
    color: var(--grey-secondary);
    padding: 0.5rem 0;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .chip:focus-visible,
  .choice:focus-visible,
  .unknown:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }

  .nav {
    margin-left: auto;
  }

  .need-answer {
    font-size: 0.8125rem;
  }

  @media (max-width: 768px) {
    .title {
      font-size: 1.25rem;
    }
  }
</style>
